import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtDate, openPdf } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { toast } from "sonner";
import { DownloadSimple, FilePdf } from "@phosphor-icons/react";

export default function DayBook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = !!(user && user.is_owner);
  const [data, setData] = useState({ rows: [], total_debit: 0, total_credit: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ start: "", end: "" });

  const load = useCallback(() => {
    setLoading(true);
    const p = {};
    if (range.start) p.start = range.start;
    if (range.end) p.end = range.end;
    api.get("/gl/daybook", { params: p }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const exportDaybook = (format) => {
    const p = new URLSearchParams();
    p.set("format", format);
    if (range.start) p.set("start", range.start);
    if (range.end) p.set("end", range.end);
    openPdf(`gl/daybook/export?${p.toString()}`);
  };

  const openSource = async (r) => {
    if (!isOwner || !r.source_type || !r.source_id) return;
    try {
      const { data: link } = await api.get("/gl/source-link", { params: { source_type: r.source_type, source_id: r.source_id } });
      if (link?.route) navigate(link.route);
      else toast.info("This posting has no editable source document.");
    } catch { toast.error("Couldn't open the source document."); }
  };

  return (
    <Layout>
      <PageHeader title="Day Book" subtitle="Every general-ledger posting, in date order" />
      <div className="p-8" data-testid="daybook-page">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div><Label className="lx-label">From</Label><DateField className="mt-1 w-44" data-testid="daybook-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} /></div>
          <div><Label className="lx-label">To</Label><DateField className="mt-1 w-44" data-testid="daybook-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /></div>
          <Button variant="outline" size="sm" data-testid="daybook-export-csv" onClick={() => exportDaybook("csv")}><DownloadSimple size={15} className="mr-1" />CSV</Button>
          <Button variant="outline" size="sm" data-testid="daybook-export-pdf" onClick={() => exportDaybook("pdf")}><FilePdf size={15} className="mr-1" />PDF</Button>
          <div className="ml-auto flex gap-6 text-sm">
            <div><span className="lx-label text-gray-400">Postings</span> <span className="lx-num ml-1 font-semibold">{data.rows.length}</span></div>
            <div><span className="lx-label text-gray-400">Total Dr</span> <span className="lx-num ml-1 font-semibold text-emerald-700">{fmtNum(data.total_debit, "TZS")}</span></div>
            <div><span className="lx-label text-gray-400">Total Cr</span> <span className="lx-num ml-1 font-semibold text-emerald-700">{fmtNum(data.total_credit, "TZS")}</span></div>
          </div>
        </div>
        {isOwner && <p className="mb-2 text-xs text-gray-500" data-testid="daybook-owner-hint">Click a row to open its source document, where you can edit or delete the transaction.</p>}
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Reference</th>
              <th className="px-4 py-2 text-left">Narration</th>
              <th className="px-4 py-2 text-left">Account</th>
              <th className="px-4 py-2 text-right">Debit</th>
              <th className="px-4 py-2 text-right">Credit</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
              {!loading && data.rows.length === 0 && <TableEmpty colSpan={6} title="No postings in this range" message="Adjust the date range to see ledger activity." />}
              {data.rows.map((r, i) => (
                <tr key={i} data-testid={`daybook-row-${i}`} onClick={() => openSource(r)} className={`border-b border-gray-100 hover:bg-gray-50 ${isOwner ? "cursor-pointer" : ""}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2"><span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">{(r.source_type || "").replace(/_/g, " ")}</span> <span className="font-mono-num text-gray-400">{r.reference}</span></td>
                  <td className="px-4 py-2 text-gray-600">{r.memo}</td>
                  <td className="px-4 py-2"><span className="font-mono-num text-gray-400">{r.account_code}</span> · {r.account_name}</td>
                  <td className="px-4 py-2 text-right lx-num">{r.debit ? fmtNum(r.debit, "TZS") : ""}</td>
                  <td className="px-4 py-2 text-right lx-num">{r.credit ? fmtNum(r.credit, "TZS") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
